// useMaxlengthPolyfill.ts
import { useEffect } from "react";

export default function useMaxlengthPolyfill() {
  useEffect(() => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    // 조합 중인 엘리먼트 추적
    const composing = new WeakSet<EventTarget>();

    // 이모지/서로게이트 대응 (간단 코드포인트 기준)
    const clampByChars = (s: string, n: number) => Array.from(s).slice(0, n).join("");

    const hasMax = (el: HTMLInputElement | HTMLTextAreaElement) =>
      el.maxLength !== undefined && el.maxLength > -1; // -1이면 미설정

    const clampEl = (el: HTMLInputElement | HTMLTextAreaElement) => {
      if (!hasMax(el)) return;
      const max = el.maxLength;
      const next = clampByChars(el.value, max);
      if (el.value !== next) {
        // 커서 위치 보존 시도
        const start = el.selectionStart;
        const end = el.selectionEnd;
        el.value = next;
        if (start != null && end != null) {
          const pos = Math.min(next.length, start, end);
          try { el.setSelectionRange(pos, pos); } catch {}
        }
        // ⚠️ 필요 시 아래 주석을 해제하면 리스너들에게 변경 통지 가능(루프 위험 시 그대로 두세요)
        // el.dispatchEvent(new Event("input", { bubbles: true }));
      }
    };

    const onInput = (e: Event) => {
      const el = e.target as HTMLInputElement | HTMLTextAreaElement;
      if (!el || !("value" in el)) return;
      if (!hasMax(el)) return;
      if (el.hasAttribute("data-skip-maxlength-polyfill")) return; // opt-out
      if (composing.has(el)) return; // 조합 중엔 자르지 않음
      clampEl(el);
    };

    const onCompStart = (e: CompositionEvent) => {
      composing.add(e.target as EventTarget);
    };

    const onCompEnd = (e: CompositionEvent) => {
      const el = e.target as HTMLInputElement | HTMLTextAreaElement;
      composing.delete(el);
      if (!el || !hasMax(el)) return;
      if (el.hasAttribute("data-skip-maxlength-polyfill")) return;
      clampEl(el);
    };

    // 안드로이드 WebView/일부 IME: keyup 229로 주기적 보정
    const onKeyUp = (e: KeyboardEvent) => {
      if (!isAndroid) return;
      const el = e.target as HTMLInputElement | HTMLTextAreaElement;
      if (!el || !hasMax(el)) return;
      if (el.hasAttribute("data-skip-maxlength-polyfill")) return;
      const code = (e as any).keyCode ?? (e as any).which;
      if (code === 229) clampEl(el);
    };

    // 포커스 빠질 때 최종 보정
    const onBlur = (e: FocusEvent) => {
      const el = e.target as HTMLInputElement | HTMLTextAreaElement;
      if (!el || !hasMax(el)) return;
      if (el.hasAttribute("data-skip-maxlength-polyfill")) return;
      clampEl(el);
    };

    // 이벤트 위임(동적 생성된 input/textarea도 자동 커버)
    document.addEventListener("input", onInput, true);
    document.addEventListener("compositionstart", onCompStart, true);
    document.addEventListener("compositionend", onCompEnd, true);
    document.addEventListener("keyup", onKeyUp, true);
    document.addEventListener("blur", onBlur, true);

    return () => {
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("compositionstart", onCompStart, true);
      document.removeEventListener("compositionend", onCompEnd, true);
      document.removeEventListener("keyup", onKeyUp, true);
      document.removeEventListener("blur", onBlur, true);
    };
  }, []);
}
